'use client';

import { useTranslations } from 'next-intl';
import { Upload, CheckCircle } from 'lucide-react';
import { Button } from '@/shared/ui/shadcn/button';
import { Progress } from '@/shared/ui/shadcn/progress';
import type { UploadProgress, PreloadProgress } from './types';

interface UploadingPhaseProps {
  uploadProgress: UploadProgress;
}

export function UploadingPhaseContent({ uploadProgress }: UploadingPhaseProps) {
  const t = useTranslations('angle.upload.modal');

  return (
    <div className="space-y-4 flex-1 flex flex-col justify-center">
      <div className="flex items-center justify-center p-8">
        <div className="relative">
          <Upload className="h-12 w-12 text-primary animate-pulse" />
        </div>
      </div>
      <div className="space-y-2">
        <p className="text-form-help text-center text-muted-foreground">
          {t('uploadingFiles', {
            currentFile: uploadProgress.currentFile,
            totalFiles: uploadProgress.totalFiles,
          })}
        </p>
        {/* Real aggregated S3 byte progress — see AngleUpload store selector */}
        <Progress value={uploadProgress.percentage} className="w-full" />
        <p className="text-progress-meta text-center text-muted-foreground">
          {t('percentComplete', { progress: uploadProgress.percentage })}
        </p>
      </div>
    </div>
  );
}

interface LoadingPreviewPhaseProps {
  hasCompletedPreview: boolean;
  preloadProgress: PreloadProgress;
}

export function LoadingPreviewPhaseContent({
  hasCompletedPreview,
  preloadProgress,
}: LoadingPreviewPhaseProps) {
  const t = useTranslations('angle.upload.modal');

  return (
    <div className="flex-1 flex flex-col justify-center space-y-4">
      <div className="flex items-center justify-center p-8">
        <div className="relative">
          <Upload className="h-12 w-12 text-primary animate-pulse" />
        </div>
      </div>
      <div className="space-y-2">
        <p className="text-form-help text-center text-muted-foreground">
          {hasCompletedPreview
            ? t('uploadComplete')
            : t('loadingPreviewImages')}
        </p>
        {!hasCompletedPreview && preloadProgress.total > 0 && (
          <p className="text-progress-meta text-center text-muted-foreground">
            {t('imagesLoaded', {
              loaded: preloadProgress.loaded,
              total: preloadProgress.total,
            })}
          </p>
        )}
      </div>
    </div>
  );
}

interface CompletePhaseProps {
  onClose: () => void;
}

export function CompletePhaseContent({ onClose }: CompletePhaseProps) {
  const t = useTranslations('angle.upload.modal');

  return (
    <div className="space-y-4 flex-1 flex flex-col justify-center">
      <div className="flex items-center justify-center p-8">
        <CheckCircle className="h-16 w-16 text-primary" />
      </div>
      <p className="text-center text-muted-foreground">{t('successMessage')}</p>
      <div className="flex justify-center">
        <Button onClick={onClose}>{t('continue')}</Button>
      </div>
    </div>
  );
}

interface IdlePhaseProps {
  onCancel: () => void;
}

export function IdlePhaseContent({ onCancel }: IdlePhaseProps) {
  const t = useTranslations('angle.upload.modal');

  return (
    <div className="space-y-4 flex-1 flex flex-col justify-center">
      <div className="flex items-center justify-center p-8">
        <Upload className="h-16 w-16 text-muted-foreground" />
      </div>
      <p className="text-center text-muted-foreground">
        {t('selectNewImages')}
      </p>
      <div className="flex justify-center">
        <Button variant="outline" onClick={onCancel}>
          {t('cancel')}
        </Button>
      </div>
    </div>
  );
}
